import { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "../../contexts/AuthContext";
import {
  Megaphone,
  Users,
  Send,
  Eye,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  X,
  Sparkles,
  Info,
  FileText,
} from "lucide-react";

const CATEGORIES = ["all", "pilgrim", "volunteer", "vendor", "media"];
const STATUSES = ["all", "confirmed", "approved", "pending", "rejected"];

export default function Broadcasts() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [personalize, setPersonalize] = useState(true);
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [allStates, setAllStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateField, setDateField] = useState("created_at");

  // Template picker
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [previewMode, setPreviewMode] = useState(false);

  // Load available states + templates once
  useEffect(() => {
    (async () => {
      try {
        const [st, tp] = await Promise.all([
          adminApi.get("/admin/broadcasts/states"),
          adminApi.get("/admin/email-templates"),
        ]);
        setAllStates(st.data.states || []);
        // Only show broadcast-category templates in the picker (plus custom)
        setTemplates((tp.data.templates || []).filter((t) => t.category !== "system"));
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  const handleLoadTemplate = async (key) => {
    setSelectedTemplate(key);
    if (!key) return;
    try {
      const { data } = await adminApi.get(`/admin/email-templates/${key}`);
      // Pre-fill subject; body is ignored since the full HTML template is used on send.
      setSubject(data.subject || "");
      // Put a hint in the body field explaining template rendering
      setBody(
        "Using template HTML — this text is only shown if personalize is off or the template doesn't include a specific paragraph. You can leave a short summary here too."
      );
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  // Auto-refresh preview when filters change
  const refreshPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const { data } = await adminApi.post("/admin/broadcasts/preview", {
        filters: {
          category,
          status,
          states: selectedStates,
          date_from: dateFrom || null,
          date_to: dateTo || null,
          date_field: dateField,
        },
      });
      setPreview(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setPreviewLoading(false);
    }
  }, [category, status, selectedStates, dateFrom, dateTo, dateField]);

  useEffect(() => {
    refreshPreview();
  }, [refreshPreview]);

  const toggleState = (s) => {
    setSelectedStates((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const canSend =
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    (preview?.recipient_count || 0) > 0 &&
    !sending;

  const needsExtraConfirm = (preview?.recipient_count || 0) > 100;

  const handleSendClick = () => {
    setError("");
    if (!canSend) return;
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    setSending(true);
    setError("");
    try {
      const { data } = await adminApi.post("/admin/broadcasts/send", {
        filters: {
          category,
          status,
          states: selectedStates,
          date_from: dateFrom || null,
          date_to: dateTo || null,
          date_field: dateField,
        },
        subject,
        body,
        personalize,
        template_key: selectedTemplate || null,
      });
      setResult(data);
      setConfirmOpen(false);
      // Reset compose
      setSubject("");
      setBody("");
      refreshPreview();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const personalizedPreviewSubject = personalize
    ? subject.replace(/{{name}}/g, preview?.samples?.[0]?.name || "Friend")
    : subject;
  const personalizedPreviewBody = personalize
    ? body.replace(/{{name}}/g, preview?.samples?.[0]?.name || "Friend")
    : body;

  return (
    <div className="space-y-5" data-testid="broadcasts-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Eczar, serif" }}>
          Broadcast Email
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Send an announcement to a segment of registered pilgrims, volunteers, vendors, or media.
        </p>
      </div>

      {error && (
        <div className="px-4 py-2.5 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {result && (
        <div
          className="px-4 py-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm flex items-start gap-2"
          data-testid="broadcast-result"
        >
          <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">Broadcast complete!</p>
            <p className="text-xs mt-1">
              {result.recipient_count} recipient{result.recipient_count !== 1 ? "s" : ""}: {" "}
              <span className="font-medium">{result.sent} sent</span>
              {result.logged > 0 && <>, <span className="font-medium">{result.logged} logged (no SMTP)</span></>}
              {result.failed > 0 && <>, <span className="font-medium text-red-700">{result.failed} failed</span></>}
            </p>
          </div>
          <button
            onClick={() => setResult(null)}
            className="p-1 hover:bg-green-100 rounded"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Compose (left 2/3) */}
        <div className="lg:col-span-2 space-y-5">
          {/* Compose */}
          <section className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Megaphone className="w-4 h-4 text-[#FF9933]" />
              <h2 className="text-sm font-semibold text-gray-900">Compose Message</h2>
              <button
                type="button"
                onClick={() => setPreviewMode((v) => !v)}
                className="ml-auto inline-flex items-center gap-1 text-xs text-gray-600 hover:text-[#FF9933] px-2 py-1 hover:bg-orange-50 rounded transition-colors"
                data-testid="toggle-preview-btn"
              >
                <Eye className="w-3 h-3" /> {previewMode ? "Edit" : "Preview"}
              </button>
            </div>

            {!previewMode ? (
              <div className="space-y-4">
                {/* Template picker */}
                {templates.length > 0 && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-md">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-purple-900 mb-1.5 flex items-center gap-1.5">
                      <FileText className="w-3 h-3" />
                      Load from Template (optional)
                    </label>
                    <select
                      value={selectedTemplate}
                      onChange={(e) => handleLoadTemplate(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-purple-200 bg-white rounded-md focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                      data-testid="template-picker"
                    >
                      <option value="">— Write custom message (no template) —</option>
                      {templates.map((t) => (
                        <option key={t.key} value={t.key}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-purple-700 mt-1.5">
                      Templates use fully-branded HTML. Edit them in <strong>Email Templates</strong>.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="e.g. Volunteer Training Schedule — March 2028"
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none"
                    data-testid="broadcast-subject-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                    Message Body
                  </label>
                  <textarea
                    rows={10}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={"Hi {{name}},\n\nWe have exciting updates for you about Ujjain Maha Kumbh 2028...\n\nHar Har Mahadev!"}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none resize-y font-mono"
                    data-testid="broadcast-body-input"
                  />
                  <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3 h-3" />
                    Line breaks are preserved. Use blank lines to separate paragraphs.
                  </p>
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={personalize}
                    onChange={(e) => setPersonalize(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#FF9933] cursor-pointer"
                    data-testid="personalize-toggle"
                  />
                  <div className="text-sm">
                    <span className="font-medium text-gray-900 flex items-center gap-1">
                      <Sparkles className="w-3.5 h-3.5 text-[#FF9933]" />
                      Personalize with recipient name
                    </span>
                    <span className="text-xs text-gray-500">
                      Replaces <code className="bg-gray-100 px-1 rounded">{"{{name}}"}</code> in subject and body with each recipient's name.
                    </span>
                  </div>
                </label>
              </div>
            ) : (
              <div data-testid="broadcast-preview-mode">
                <div className="mb-3 px-3 py-2 bg-orange-50 border border-orange-200 rounded-md text-xs text-orange-800">
                  <strong>Preview</strong> — as it would appear to{" "}
                  <span className="font-semibold">{preview?.samples?.[0]?.name || "a recipient"}</span>
                </div>
                <div className="border border-gray-200 rounded-md overflow-hidden bg-[#1c1917]">
                  <div className="px-4 py-2 bg-[#292524] border-b border-[#3f3f3f] text-xs text-white/60">
                    <div><span className="text-white/40">Subject:</span> <span className="text-white">{personalizedPreviewSubject || "(no subject)"}</span></div>
                  </div>
                  <div className="p-6 text-white">
                    <div className="text-xl text-[#FF9933] font-semibold mb-3">
                      {personalize ? `Jai Mahakal, ${preview?.samples?.[0]?.name || "Friend"}!` : "Jai Mahakal!"}
                    </div>
                    <div className="text-sm text-white/70 whitespace-pre-wrap leading-relaxed">
                      {personalizedPreviewBody || <span className="italic text-white/30">(empty body)</span>}
                    </div>
                    <div className="mt-6 pt-4 border-t border-white/10 text-xs text-white/40 text-center">
                      Har Har Mahadev | हर हर महादेव
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Audience (right 1/3) */}
        <div className="space-y-5">
          <section className="bg-white border border-gray-200 rounded-lg p-6 sticky top-16">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-[#FF9933]" />
              <h2 className="text-sm font-semibold text-gray-900">Audience</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none bg-white capitalize"
                  data-testid="audience-category"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c === "all" ? "All Categories" : c}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none bg-white capitalize"
                  data-testid="audience-status"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s === "all" ? "All Statuses" : s}
                    </option>
                  ))}
                </select>
              </div>

              {allStates.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                    States {selectedStates.length > 0 && `(${selectedStates.length})`}
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1" data-testid="audience-states">
                    {allStates.map((s) => (
                      <label key={s} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 px-1.5 py-0.5 rounded">
                        <input
                          type="checkbox"
                          checked={selectedStates.includes(s)}
                          onChange={() => toggleState(s)}
                          className="w-3.5 h-3.5 accent-[#FF9933]"
                        />
                        {s}
                      </label>
                    ))}
                  </div>
                  {selectedStates.length === 0 && (
                    <p className="text-xs text-gray-400 mt-1">No states selected = all states</p>
                  )}
                </div>
              )}

              {/* Date Range Filter */}
              <div data-testid="audience-date-range">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Date Range
                  </label>
                  <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-[10px]" data-testid="date-field-toggle">
                    <button
                      type="button"
                      onClick={() => setDateField("created_at")}
                      className={`px-2 py-0.5 transition-colors ${
                        dateField === "created_at"
                          ? "bg-[#FF9933] text-white"
                          : "bg-white text-gray-500 hover:bg-gray-50"
                      }`}
                      data-testid="date-field-created"
                    >
                      Registered
                    </button>
                    <button
                      type="button"
                      onClick={() => setDateField("updated_at")}
                      className={`px-2 py-0.5 transition-colors border-l border-gray-200 ${
                        dateField === "updated_at"
                          ? "bg-[#FF9933] text-white"
                          : "bg-white text-gray-500 hover:bg-gray-50"
                      }`}
                      data-testid="date-field-updated"
                    >
                      Updated
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">From</label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      max={dateTo || undefined}
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none bg-white"
                      data-testid="date-from"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">To</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      min={dateFrom || undefined}
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none bg-white"
                      data-testid="date-to"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {[
                    { label: "Today", days: 0 },
                    { label: "7d", days: 6 },
                    { label: "30d", days: 29 },
                    { label: "90d", days: 89 },
                  ].map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => {
                        const today = new Date();
                        const from = new Date();
                        from.setDate(today.getDate() - p.days);
                        const iso = (d) => d.toISOString().slice(0, 10);
                        setDateFrom(iso(from));
                        setDateTo(iso(today));
                      }}
                      className="px-2 py-0.5 text-[10px] rounded border border-gray-200 text-gray-600 hover:bg-[#FF9933]/10 hover:border-[#FF9933]/40 hover:text-[#FF9933]"
                      data-testid={`date-preset-${p.label.toLowerCase()}`}
                    >
                      {p.label}
                    </button>
                  ))}
                  {(dateFrom || dateTo) && (
                    <button
                      type="button"
                      onClick={() => {
                        setDateFrom("");
                        setDateTo("");
                      }}
                      className="px-2 py-0.5 text-[10px] rounded border border-transparent text-gray-400 hover:text-red-500"
                      data-testid="date-clear"
                    >
                      Clear ×
                    </button>
                  )}
                </div>
                {dateField === "updated_at" && (
                  <p className="text-[10px] text-gray-400 mt-1.5" data-testid="updated-at-hint">
                    Shows only registrations whose status was changed in this range.
                  </p>
                )}
              </div>
            </div>

            {/* Recipient count */}
            <div className="mt-5 pt-5 border-t border-gray-100" data-testid="recipient-count-box">
              {previewLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin text-[#FF9933]" />
                  Calculating...
                </div>
              ) : (
                <div>
                  <p className="text-3xl font-bold text-gray-900" data-testid="recipient-count">
                    {preview?.recipient_count ?? 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    recipient{(preview?.recipient_count || 0) !== 1 ? "s" : ""} will receive this email
                  </p>
                  {(preview?.without_email || 0) > 0 && (
                    <p className="text-xs text-amber-600 mt-1.5 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                      {preview.without_email} matching registrant{preview.without_email !== 1 ? "s have" : " has"} no email on file
                    </p>
                  )}
                </div>
              )}

              <button
                onClick={handleSendClick}
                disabled={!canSend}
                className={`w-full mt-4 py-2.5 rounded-md text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                  canSend
                    ? "bg-[#FF9933] hover:bg-[#E67300] text-white shadow-sm"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
                data-testid="send-broadcast-btn"
              >
                <Send className="w-4 h-4" />
                Send Broadcast
              </button>
              <p className="text-xs text-gray-400 mt-2 text-center">You'll be asked to confirm.</p>
            </div>
          </section>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="confirm-modal"
        >
          <div className="bg-white rounded-lg max-w-lg w-full shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Broadcast</h3>
              <button
                onClick={() => !sending && setConfirmOpen(false)}
                disabled={sending}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-center py-3">
                <p className="text-4xl font-bold text-[#FF9933]">{preview?.recipient_count}</p>
                <p className="text-sm text-gray-600 mt-1">
                  recipient{(preview?.recipient_count || 0) !== 1 ? "s" : ""} will receive this email
                </p>
              </div>

              <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Subject</p>
                <p className="text-sm text-gray-900 font-medium">{subject}</p>
              </div>

              {needsExtraConfirm && (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded-md flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    <strong>This is a large broadcast</strong> ({preview?.recipient_count} recipients).
                    Please double-check the subject and audience before sending. This action cannot be undone.
                  </p>
                </div>
              )}

              <div className="text-xs text-gray-500">
                Filters: <span className="font-medium capitalize">{category}</span> · <span className="font-medium capitalize">{status}</span>
                {selectedStates.length > 0 && <> · {selectedStates.length} state{selectedStates.length !== 1 ? "s" : ""}</>}
                {(dateFrom || dateTo) && (
                  <> · {dateField === "updated_at" ? "Updated" : "Registered"} {dateFrom || "…"} → {dateTo || "…"}</>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={sending}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-md font-medium disabled:opacity-50"
                data-testid="cancel-send-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSend}
                disabled={sending}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-[#FF9933] hover:bg-[#E67300] text-white rounded-md font-semibold disabled:opacity-60 shadow-sm"
                data-testid="confirm-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Sending..." : needsExtraConfirm ? "Yes, send it" : "Send Now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
